import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Building2, Briefcase, CalendarIcon, ChevronRight, Loader2,
  MapPin, Sparkles, Pencil, Save, X as XIcon, Trash2, Mic, User as UserIcon,
  Lock, CheckCircle2, ExternalLink, MessageSquare, Phone, ArrowLeft,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "../components/ui/sonner";
import { useAuth } from "../context/AuthContext";
import DateTimePicker from "../components/DateTimePicker";
import SearchableSelect from "../components/SearchableSelect";
import VoiceTextarea from "../components/VoiceTextarea";
import { EntityShell } from "../components/shell";
import HistoryBlock from "../components/shell/HistoryBlock";

/**
 * MeetingDetail — Wave 19 #110 (full-page replacement for MeetingDetailModal).
 *
 * Two modes, auto-picked:
 *   • EDITOR  — meeting has no saved outcome yet. All fields editable
 *               (title, start, end, description, customer/lead/site,
 *               attendees). Debrief capture form at the bottom with
 *               voice dictation. Saving the debrief LOCKS the meeting.
 *   • VIEWER  — meeting has ≥1 outcome. All fields read-only. Outcomes
 *               listed in full. Append-only comments thread at the
 *               bottom with voice dictation + author/timestamp stamp.
 *
 * Contract ties:
 *   • Customer Foundation — breadcrumb pill customer → lead → meeting.
 *   • Voice Dictation UI Consistency §13 — every long-text uses
 *     <VoiceTextarea>.
 *   • Full-Page Editor §7 — primary entity views are pages, not modals.
 */
const OUTCOME_OPTIONS = [
  { v: "", label: "— outcome —" },
  { v: "warm", label: "warm" },
  { v: "hot", label: "hot" },
  { v: "cold", label: "cold" },
  { v: "won", label: "won" },
  { v: "lost", label: "lost" },
  { v: "follow_up", label: "follow up" },
  { v: "no_show", label: "no show" },
];

const fmtDt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Editor form state
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Debrief capture state
  const [dSummary, setDSummary] = useState("");
  const [dOutcome, setDOutcome] = useState("");
  const [dNext, setDNext] = useState("");
  const [dVoiceRaw, setDVoiceRaw] = useState("");
  const [debriefing, setDebriefing] = useState(false);

  // Comment thread state (viewer mode)
  const [commentBody, setCommentBody] = useState("");
  const [commentVoiceRaw, setCommentVoiceRaw] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  // Option sources (editor mode)
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [sites, setSites] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/meetings/${id}`);
      setM(data);
      setForm({
        title: data.title || "",
        description: data.description || "",
        start: data.start || "",
        end: data.end || "",
        customer_id: data.customer_id || "",
        site_id: data.site_id || "",
        lead_id: data.lead_id || "",
        attendees: (data.attendees || []).join(", "),
      });
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load meeting");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!id) return;
    load();
    // eslint-disable-next-line
  }, [id]);

  // Load customers + leads lazily once we're in edit mode
  useEffect(() => {
    const isLocked = (m?.outcomes || []).length > 0;
    if (!m || isLocked) return;
    let cancelled = false;
    (async () => {
      try {
        const [c, l] = await Promise.all([api.get("/customers"), api.get("/leads")]);
        if (cancelled) return;
        setCustomers(c.data || []);
        setLeads(l.data || []);
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [m]);

  // Load sites when customer changes
  useEffect(() => {
    if (!form?.customer_id) { setSites([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/sites?customer_id=${form.customer_id}`);
        if (!cancelled) setSites(data || []);
      } catch (_e) { if (!cancelled) setSites([]); }
    })();
    return () => { cancelled = true; };
  }, [form?.customer_id]);

  const isLocked = (m?.outcomes || []).length > 0;

  const matchingLeads = useMemo(() => {
    const cid = form?.customer_id;
    return leads.filter((l) => !cid || l.customer_id === cid).slice(0, 200);
  }, [leads, form?.customer_id]);

  // ─── Actions ────────────────────────────────────────────────────────
  const saveFields = async () => {
    if (!form) return;
    if (!form.title?.trim()) {
      toast.error("Title is required to save the meeting.");
      return;
    }
    if (!form.start) {
      toast.error("Start time is required to save the meeting.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description,
        start: form.start,
        end: form.end || form.start,
        customer_id: form.customer_id || null,
        site_id: form.site_id || null,
        lead_id: form.lead_id || null,
        attendees: form.attendees
          ? form.attendees.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      await api.patch(`/meetings/${id}`, payload);
      toast.success("Meeting saved.");
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      await load();
    } catch (e) {
      const msg = formatErr(e?.response?.data?.detail, "Could not save meeting");
      setErr(msg);
      toast.error(msg);
    }
    setSaving(false);
  };

  const saveDebrief = async () => {
    if (!dSummary.trim()) {
      setErr("Add a short summary before saving the debrief.");
      return;
    }
    setDebriefing(true);
    setErr("");
    try {
      await api.post(`/meetings/${id}/outcome`, {
        summary: dSummary.trim(),
        outcome: dOutcome,
        next_step: dNext.trim(),
        voice_raw: dVoiceRaw || null,
      });
      setDSummary("");
      setDOutcome("");
      setDNext("");
      setDVoiceRaw("");
      await load(); // reloads → flips to viewer mode (locked)
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save debrief");
    }
    setDebriefing(false);
  };

  const postComment = async () => {
    if (!commentBody.trim()) return;
    setPostingComment(true);
    setErr("");
    try {
      await api.post(`/meetings/${id}/comment`, {
        body: commentBody.trim(),
        voice_raw: commentVoiceRaw || null,
      });
      setCommentBody("");
      setCommentVoiceRaw("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not post comment");
    }
    setPostingComment(false);
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const doDelete = async () => {
    try {
      await api.delete(`/meetings/${id}`);
      navigate(-1);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not delete meeting");
    }
  };
  const [tab, setTab] = useState("overview");

  // ─── Render ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 text-center text-xs text-[#647087]" data-testid="meeting-detail-loading">
        <Loader2 className="w-5 h-5 animate-spin inline mr-1" /> Loading meeting…
      </div>
    );
  }
  if (!m) {
    return (
      <div className="p-6 text-center text-xs text-[#647087]" data-testid="meeting-detail-not-found">
        {err || "Meeting not found."}
        <div className="mt-3">
          <button onClick={() => navigate(-1)} className="hv-btn hv-btn-ghost text-xs">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
        </div>
      </div>
    );
  }

  // Phase 7 Sub-iter 7G — EntityShell migration.
  const meetingTabs = [
    { key: "overview", label: "Overview" },
    isLocked ? { key: "comments", label: "Comments", count: (m.comments || []).length || undefined } : null,
    { key: "activity", label: "Activity" },
  ].filter(Boolean);
  const headerActions = (
    <>
      {!isLocked && !confirmDelete && (
        <button
          onClick={() => setConfirmDelete(true)}
          className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:bg-rose-500/10"
          data-testid="meeting-delete"
          title="Delete meeting"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      )}
      {!isLocked && confirmDelete && (
        <>
          <button
            onClick={() => setConfirmDelete(false)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="meeting-delete-cancel"
          >
            Cancel
          </button>
          <button
            onClick={doDelete}
            className="hv-btn hv-btn-primary bg-rose-500 hover:bg-rose-400 text-xs"
            data-testid="meeting-delete-confirm"
          >
            <Trash2 className="w-3 h-3" /> Confirm delete
          </button>
        </>
      )}
    </>
  );

  return (
    <EntityShell
      kindLabel="Meeting"
      title={m.title || "Meeting"}
      status={{
        label: isLocked ? "locked" : "editable",
        tone: isLocked ? "amber" : "blue",
      }}
      customerLink={m.customer ? {
        label: `View customer · ${m.customer.name}`,
        to: `/customers/${m.customer.id}`,
      } : undefined}
      backTo="/meetings"
      actions={headerActions}
      tabs={meetingTabs}
      activeTab={tab}
      onTabChange={setTab}
    >
    <div className="space-y-4" data-testid="meeting-detail-page">
      {/* ─── Customer Foundation breadcrumb ─────────────────────────── */}
      <div
        className="flex items-center flex-wrap gap-1 text-[11px] font-mono"
        data-testid="meeting-breadcrumb"
      >
        {m.customer ? (
          <Link
            to={`/customers/${m.customer.id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#1FB6C1]/40 bg-[#1FB6C1]/10 text-[#1FB6C1] hover:bg-[#1FB6C1]/20"
            data-testid="meeting-bc-customer"
          >
            <Building2 className="w-3 h-3" /> {m.customer.name}
          </Link>
        ) : (
          <span className="text-[#647087] italic px-2 py-0.5">No customer linked</span>
        )}
        {m.lead && (
          <>
            <ChevronRight className="w-3 h-3 text-[#647087]" />
            <Link
              to={`/leads/${m.lead.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#C4B5FD] hover:bg-[#A78BFA]/20"
              data-testid="meeting-bc-lead"
            >
              <Briefcase className="w-3 h-3" /> {m.lead.title || m.lead.customer_name || "Lead"}
              {m.lead.phase && (
                <span className="text-[9px] ml-1 px-1 py-0 rounded bg-[#A78BFA]/20 uppercase">{m.lead.phase}</span>
              )}
            </Link>
          </>
        )}
        {m.trip && (
          <>
            <ChevronRight className="w-3 h-3 text-[#647087]" />
            <Link
              to={`/trips/${m.trip.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
              data-testid="meeting-bc-trip"
            >
              <CalendarIcon className="w-3 h-3" /> {m.trip.title || "Trip"}
            </Link>
          </>
        )}
        <ChevronRight className="w-3 h-3 text-[#647087]" />
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/15 bg-white/5 text-[#dde3ec]">
          <CalendarIcon className="w-3 h-3" /> Meeting
        </span>
      </div>

      {err && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5" data-testid="meeting-err">
          {err}
        </div>
      )}

      {tab === "overview" && (
        <>
          {/* ─── Fields panel ─────────────────────────────────────────── */}
          {isLocked ? (
            <LockedFields m={m} />
          ) : (
            <EditorFields
              form={form}
              setForm={setForm}
              customers={customers}
              sites={sites}
              matchingLeads={matchingLeads}
              onSave={saveFields}
              saving={saving}
              savedFlash={savedFlash}
            />
          )}

          {/* ─── Debrief section ───────────────────────────────────── */}
          {!isLocked ? (
            <DebriefCaptureForm
              dSummary={dSummary}
              setDSummary={setDSummary}
              dOutcome={dOutcome}
              setDOutcome={setDOutcome}
              dNext={dNext}
              setDNext={setDNext}
              setDVoiceRaw={setDVoiceRaw}
              saveDebrief={saveDebrief}
              debriefing={debriefing}
            />
          ) : (
            <OutcomesList outcomes={m.outcomes} />
          )}

          {/* ─── Quick links ──────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {m.lead && (
              <Link
                to={`/leads/${m.lead.id}`}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="meeting-open-lead"
              >
                <ExternalLink className="w-3 h-3" /> Open lead
              </Link>
            )}
            {m.customer && (
              <Link
                to={`/customers/${m.customer.id}`}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="meeting-open-customer"
              >
                <ExternalLink className="w-3 h-3" /> Open customer
              </Link>
            )}
            {m.lead?.own_contact_phone && (
              <a
                href={`https://wa.me/${(m.lead.own_contact_phone || "").replace(/[^0-9+]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="meeting-call-contact"
              >
                <Phone className="w-3 h-3" /> {m.lead.own_contact_name || "Contact"}
              </a>
            )}
          </div>
        </>
      )}

      {/* ─── Comments thread (locked only — append-only) ──────────── */}
      {tab === "comments" && isLocked && (
        <CommentsThread
          comments={m.comments || []}
          commentBody={commentBody}
          setCommentBody={setCommentBody}
          setCommentVoiceRaw={setCommentVoiceRaw}
          postComment={postComment}
          postingComment={postingComment}
          me={user}
        />
      )}

      {tab === "activity" && (
        <HistoryBlock entity={{ id, kind: "meeting" }} />
      )}
    </div>
    </EntityShell>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────

function LockedFields({ m }) {
  return (
    <div className="hv-card p-4 space-y-3" data-testid="meeting-fields-locked">
      <div className="font-display text-xl md:text-2xl font-bold">{m.title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        <Field label="Start">{fmtDt(m.start)}</Field>
        <Field label="End">{fmtDt(m.end)}</Field>
        {m.organizer?.name && <Field label="Organizer">{m.organizer.name}</Field>}
        {(m.attendees || []).length > 0 && <Field label="Attendees">{m.attendees.join(", ")}</Field>}
        {m.site?.address && <Field label="Site"><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.site.address}</span></Field>}
        {m.latest_outcome && <Field label="Latest outcome"><span className="text-emerald-300 uppercase font-mono">{m.latest_outcome}</span></Field>}
      </div>
      {m.description && (
        <div className="border-t border-white/5 pt-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold mb-1">Description</div>
          <div className="text-sm text-[#dde3ec] whitespace-pre-wrap leading-relaxed">{m.description}</div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">{label}</div>
      <div className="text-[#dde3ec]">{children}</div>
    </div>
  );
}

function EditorFields({ form, setForm, customers, sites, matchingLeads, onSave, saving, savedFlash }) {
  return (
    <div className="hv-card p-4 space-y-3" data-testid="meeting-fields-editor">
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">Title</label>
        <input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="hv-input mt-1"
          data-testid="meeting-input-title"
          placeholder="Site visit / discovery call / proposal review…"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">Description</label>
        <VoiceTextarea
          value={form.description}
          onChange={(v) => setForm({ ...form, description: v })}
          rows={4}
          placeholder="Agenda, context, what to bring. Or tap the mic to dictate."
          context="lead_activity"
          testIdPrefix="meeting-desc-voice"
          data-testid="meeting-input-description"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">Start</label>
          <DateTimePicker
            value={form.start}
            onChange={(v) => setForm({ ...form, start: v })}
            testId="meeting-input-start"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">End</label>
          <DateTimePicker
            value={form.end}
            onChange={(v) => setForm({ ...form, end: v })}
            testId="meeting-input-end"
          />
        </div>
      </div>

      <div className="border-t border-white/5 pt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] mb-2 font-bold">Linked to</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">Customer</label>
            <SearchableSelect
              value={form.customer_id || ""}
              onChange={(id) => setForm({ ...form, customer_id: id, site_id: "" })}
              options={customers.map((c) => ({
                id: c.id,
                label: c.name,
                sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.phone, c.email].filter(Boolean).join(" · "),
                searchTerms: [c.name, c.external_ref, c.phone, c.email, c.vat_number, c.short_id].filter(Boolean),
              }))}
              placeholder="— No customer —"
              testId="meeting-input-customer"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">Site</label>
            <SearchableSelect
              value={form.site_id || ""}
              onChange={(id) => setForm({ ...form, site_id: id })}
              options={sites.map((s) => ({
                id: s.id,
                label: s.name,
                sublabel: s.address || "",
                searchTerms: [s.name, s.address, s.external_ref].filter(Boolean),
              }))}
              placeholder={form.customer_id ? "— No specific site —" : "Pick a customer first"}
              disabled={!form.customer_id}
              testId="meeting-input-site"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">Lead</label>
            <SearchableSelect
              value={form.lead_id || ""}
              onChange={(id) => setForm({ ...form, lead_id: id })}
              options={matchingLeads.map((l) => ({
                id: l.id,
                label: l.title || l.contact_name || "Untitled lead",
                sublabel: l.customer_name || "",
                searchTerms: [l.title, l.contact_name, l.customer_name, l.industry, l.phase].filter(Boolean),
              }))}
              placeholder="— No lead —"
              testId="meeting-input-lead"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">Attendees (comma-separated emails)</label>
        <input
          value={form.attendees}
          onChange={(e) => setForm({ ...form, attendees: e.target.value })}
          className="hv-input mt-1 text-sm"
          data-testid="meeting-input-attendees"
        />
      </div>

      <div className="sticky bottom-2 z-10 flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !form.title || !form.start}
          className="hv-btn hv-btn-primary text-xs shadow-lg shadow-[#1FB6C1]/20"
          data-testid="meeting-save-fields"
        >
          {savedFlash ? <CheckCircle2 className="w-3 h-3 text-emerald-300" /> : <Save className="w-3 h-3" />}
          {saving ? "Saving…" : savedFlash ? "Saved" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function DebriefCaptureForm({ dSummary, setDSummary, dOutcome, setDOutcome, dNext, setDNext, setDVoiceRaw, saveDebrief, debriefing }) {
  return (
    <div className="hv-card p-4 border-[#A78BFA]/30 bg-[#A78BFA]/5" data-testid="meeting-debrief-form">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#C4B5FD]" />
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#C4B5FD] font-bold">
          How did this meeting go? Saving locks the meeting.
        </div>
      </div>
      <div className="space-y-3">
        <VoiceTextarea
          value={dSummary}
          onChange={setDSummary}
          onCommit={({ refined, raw, extracted }) => {
            setDSummary(refined);
            setDVoiceRaw(raw || "");
            if (extracted?.outcome) setDOutcome(extracted.outcome);
            if (extracted?.next_step) setDNext(extracted.next_step);
          }}
          context="meeting_outcome"
          rows={4}
          placeholder="Who you spoke to, what they said, key facts, decisions. Or tap the mic to dictate."
          testIdPrefix="meeting-debrief-voice"
          data-testid="meeting-debrief-summary"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <select
            value={dOutcome}
            onChange={(e) => setDOutcome(e.target.value)}
            className="hv-input text-xs"
            data-testid="meeting-debrief-tag"
          >
            {OUTCOME_OPTIONS.map((o) => (<option key={o.v} value={o.v}>{o.label}</option>))}
          </select>
          <input
            type="text"
            value={dNext}
            onChange={(e) => setDNext(e.target.value)}
            placeholder="Next step (with owner + timing)"
            className="hv-input text-xs"
            data-testid="meeting-debrief-next"
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-[#647087]">
            Saving syncs to the lead + customer activity and locks the meeting.
            Comments can still be added after.
          </span>
          <button
            type="button"
            onClick={saveDebrief}
            disabled={debriefing || !dSummary.trim()}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="meeting-debrief-save"
          >
            <Lock className="w-3 h-3" />
            {debriefing ? "Locking…" : "Save & lock"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OutcomesList({ outcomes }) {
  const list = [...(outcomes || [])].reverse();
  if (!list.length) return null;
  return (
    <div className="hv-card p-4" data-testid="meeting-outcomes-list">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#C4B5FD] font-bold mb-3 flex items-center gap-2">
        <Sparkles className="w-4 h-4" /> Debrief ({list.length})
      </div>
      <ul className="space-y-3">
        {list.map((o) => (
          <li key={o.id} className="border border-white/10 rounded-md p-3 bg-black/10">
            <div className="text-[11px] text-[#a0aabf] flex items-center gap-2">
              <UserIcon className="w-3 h-3" />
              <span>{o.by_user_name || "—"}</span>
              <span className="text-[#647087]">·</span>
              <span>{fmtDt(o.created_at)}</span>
              {o.outcome && (
                <span className="ml-auto text-[10px] uppercase font-mono text-[#A78BFA] border border-[#A78BFA]/40 bg-[#A78BFA]/10 rounded px-1.5 py-0.5">
                  {o.outcome}
                </span>
              )}
            </div>
            <div className="text-sm text-[#dde3ec] mt-2 whitespace-pre-wrap leading-relaxed">
              {o.summary}
            </div>
            {o.next_step && (
              <div className="mt-2 text-xs text-[#a0aabf]">
                <strong className="text-[#1FB6C1]">Next step:</strong> {o.next_step}
              </div>
            )}
            {o.voice_raw && <OutcomeRawToggle raw={o.voice_raw} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function OutcomeRawToggle({ raw }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 border-t border-white/5 pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[10px] text-[#647087] hover:text-[#a0aabf] inline-flex items-center gap-1"
      >
        <Mic className="w-2.5 h-2.5" />
        {open ? "Hide" : "Show"} original transcript
      </button>
      {open && (
        <div className="mt-1 text-[11px] text-[#647087] whitespace-pre-wrap leading-relaxed bg-black/20 border border-white/5 rounded p-2 font-mono">
          {raw}
        </div>
      )}
    </div>
  );
}

function CommentsThread({ comments, commentBody, setCommentBody, setCommentVoiceRaw, postComment, postingComment }) {
  const list = [...(comments || [])].sort((a, b) =>
    (a.created_at || "").localeCompare(b.created_at || ""));
  return (
    <div className="hv-card p-4" data-testid="meeting-comments">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#1FB6C1] font-bold mb-3 flex items-center gap-2">
        <MessageSquare className="w-4 h-4" /> Additional comments ({list.length})
      </div>
      {list.length === 0 && (
        <div className="text-[11px] text-[#647087] italic mb-3">
          No comments yet. Anything you remember later goes here — stamped with your name and the time.
        </div>
      )}
      {list.length > 0 && (
        <ul className="space-y-3 mb-4">
          {list.map((c) => (
            <li key={c.id} className="border border-white/10 rounded-md p-3 bg-black/10" data-testid={`meeting-comment-${c.id}`}>
              <div className="text-[11px] text-[#a0aabf] flex items-center gap-2">
                <UserIcon className="w-3 h-3" />
                <span className="text-[#C4B5FD]">{c.author_name || "—"}</span>
                <span className="text-[#647087]">·</span>
                <span>{fmtDt(c.created_at)}</span>
              </div>
              <div className="text-sm text-[#dde3ec] mt-2 whitespace-pre-wrap leading-relaxed">
                {c.body}
              </div>
              {c.voice_raw && <OutcomeRawToggle raw={c.voice_raw} />}
            </li>
          ))}
        </ul>
      )}
      <div className="border-t border-white/5 pt-3 space-y-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold">
          Add a comment (cannot be edited once posted)
        </div>
        <VoiceTextarea
          value={commentBody}
          onChange={setCommentBody}
          onCommit={({ refined, raw }) => {
            setCommentBody(refined);
            setCommentVoiceRaw(raw || "");
          }}
          context="lead_activity"
          rows={3}
          placeholder="Something you remembered? Drop it here. Tap the mic to dictate."
          testIdPrefix="meeting-comment-voice"
          data-testid="meeting-comment-input"
        />
        <div className="flex justify-end">
          <button
            type="button"
            onClick={postComment}
            disabled={postingComment || !commentBody.trim()}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="meeting-comment-save"
          >
            {postingComment ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Post comment
          </button>
        </div>
      </div>
    </div>
  );
}
